import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import Copy from 'Src/components/Copy';
import { CheckActivityStatement } from 'Src/feature/check/CheckActivityStatement';
import { Check } from 'Src/feature/check/models';
import { useEmployee } from 'Src/feature/employee/useEmployee';

import styles from './CheckActivityRow.module.scss';

interface CheckActivityRowProps {
  activity: Check['activities'][number];
  check: Check | undefined;
}

const CheckActivityRow: FC<CheckActivityRowProps> = ({ check, activity }) => {
  const { t } = useTranslation();
  const employeeMap = useEmployee.query.map();

  const payment = ['PaymentAdded', 'PaymentUpdated', 'PaymentRemoved', 'GoodFaithPaymentReran'].includes(activity.type)
    ? (check?.payments || []).find(({ id }) => id === activity.updatedUuid)
    : undefined;

  const employee = activity.actor.performedByEmployeeId ? employeeMap.data?.get(activity.actor.performedByEmployeeId) : undefined;

  let discountAmount = '';
  if (activity.discountInfo?.type === 'Amount') {
    discountAmount = t('labels.util.currency', { value: activity.discountInfo?.value });
  } else if (activity.discountInfo?.type === 'Percent') {
    discountAmount = t('labels.util.percent', { value: activity.discountInfo?.value });
  }

  let surchargeAmount = '';
  if (activity.surchargeInfo?.type === 'FixedAmount' || activity.surchargeInfo?.type === 'OpenAmount') {
    surchargeAmount = t('labels.util.currency', { value: activity.surchargeInfo?.value });
  } else if (activity.surchargeInfo?.type === 'FixedPercent' || activity.surchargeInfo?.type === 'OpenPercent') {
    surchargeAmount = t('labels.util.currency', { value: activity.surchargeInfo?.value });
  }

  return (
    <div className={styles['root']}>
      <div className={styles['outer-grid']}>
        <Copy
          color="textSecondary"
          variant="body"
          size="sm"
          value={activity.performedAt
            ? DateTime.fromMillis(activity.performedAt).toFormat('hh:mm:ss a')
            : ''}
        />
        <Copy
          color="textSecondary"
          variant="body"
          size="sm"
          value={employee ? t('labels.util.employeeFullName', employee) : ''}
        />
        <div className={styles['grid']}>
          <CheckActivityStatement activity={activity} />

          {/* menu item info */}
          {activity.itemInfo && !activity.type.includes('GiftCard') && (
            <div className={styles['inner-grid']}>
              {/* row 1 */}
              <Copy color="textSecondary">
                <Trans
                  i18nKey="CheckActivity.menuItem"
                  values={{
                    count: activity.itemInfo.quantity || 1,
                    name: activity.itemInfo.menuItem.name,
                  }}
                />
              </Copy>
              {/* row 2 */}
              <Copy
                color="textSecondary"
                variant="body"
                size="sm"
                align="right"
                transparent={activity.itemInfo.menuItem.price <= 0}
                value={t('labels.util.currency', { value: activity.itemInfo.menuItem.override?.price ?? activity.itemInfo.menuItem.price })}
              />

              {/* modifier rows */}
              {activity.itemInfo.modifiers?.map((modifier, idx) => (
                <React.Fragment key={`${activity.itemInfo?.menuItem.menuItemId}-${modifier.modifierGroupId || modifier.modifierId}-${idx}`}>
                  <Copy
                    className={styles['modifier-name']}
                    color="textSecondary"
                    variant="body"
                    size="sm"
                    ellipsis
                    value={modifier.modifierName || modifier.modifierGroupName}
                  />
                  <Copy
                    color="textSecondary"
                    variant="body"
                    size="sm"
                    align="right"
                    transparent={modifier.price <= 0}
                    value={t('labels.util.currency', { value: modifier.price })}
                  />
                </React.Fragment>
              ))}
              {activity.itemInfo.seatNames && (
                <Copy
                  className={styles['grid-item-fill']}
                  variant="body"
                  size="sm"
                  align="right"
                  value={activity.itemInfo.seatNames}
                />
              )}
            </div>
          )}

          {/* surcharge info */}
          {!!activity.surchargeInfo && (
            <div className={styles['inner-grid']}>
              <Copy color="textSecondary" value={activity.surchargeInfo?.name} />
              <Copy
                color="textSecondary"
                variant="body"
                size="sm"
                align="right"
                value={surchargeAmount}
              />
            </div>
          )}

          {/* discount info */}
          {!!activity.discountInfo && (
            <div className={styles['inner-grid']}>
              <Copy color="textSecondary" value={activity.discountInfo?.name} />
              <Copy
                color="textSecondary"
                variant="body"
                size="sm"
                align="right"
                value={discountAmount}
              />
            </div>
          )}

          {/* payment added/removed */}
          {payment && (activity.type === 'PaymentAdded' || activity.type === 'PaymentRemoved' || activity.type === 'GoodFaithPaymentReran') && (
            <div className={styles['inner-grid']}>
              {/* row 1 */}
              <Copy
                color="textSecondary"
                variant="body"
                size="sm"
                value={[
                  // filter out any missing parts
                  payment.ccData?.cardBrand,
                  payment.ccData?.cardholderName,
                  payment.ccData?.lastFourCcNumber,
                ].filter(Boolean).join(', ')}
              />
              <Copy
                color="textSecondary"
                variant="body"
                size="sm"
                align="right"
                transparent={payment.amountAppliedToCheck <= 0}
                value={t('labels.util.currency', { value: payment.amountAppliedToCheck })}
              />
            </div>
          )}

          {/* payment added/removed */}
          {payment && activity.type === 'PaymentUpdated' && (
            <div className={styles['inner-grid']}>
              {/* row 1 */}
              <Copy color="textSecondary" variant="body" size="sm" value={t('check.activities.paymentUpdatedDetails.tip', { value: activity.tipAmount })} />
              <Copy color="textSecondary" variant="body" size="sm" value={t('check.activities.paymentUpdatedDetails.total', { value: activity.amount })} align="right" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckActivityRow;
export type { CheckActivityRowProps };
